import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../api';
import { errorText, t } from '../i18n';
import type { Difficulty } from '../types';

/**
 * End-of-lesson feedback: two taps (difficulty + useful) plus an optional
 * note. This is the explicit half of the recursive loop — the next pack,
 * exam and review mix adapt to it (see LearnStore.learnerContext()).
 */
@customElement('lesson-feedback')
export class LessonFeedback extends LitElement {
  /** `moment:<id>` or `exam:<id>`. */
  @property() scope = '';
  @state() private difficulty?: Difficulty;
  @state() private useful?: number;
  @state() private note = '';
  @state() private saved = false;
  @state() private saving = false;
  @state() private msg = '';

  protected createRenderRoot() { return this; }

  private save = async () => {
    if (!this.scope || (!this.difficulty && this.useful === undefined && !this.note.trim())) {
      this.msg = t('feedback.pickFirst');
      return;
    }
    this.saving = true;
    this.msg = '';
    try {
      await api.saveFeedback(this.scope, {
        ...(this.difficulty ? { difficulty: this.difficulty } : {}),
        ...(this.useful !== undefined ? { useful: this.useful } : {}),
        ...(this.note.trim() ? { note: this.note.trim() } : {}),
      });
      this.saved = true;
      this.msg = t('feedback.saved');
    } catch (e) {
      this.msg = errorText(e);
    } finally {
      this.saving = false;
    }
  };

  render() {
    const diff: { v: Difficulty; label: string }[] = [
      { v: 'too_easy', label: t('diff.too_easy') },
      { v: 'just_right', label: t('diff.just_right') },
      { v: 'too_hard', label: t('diff.too_hard') },
    ];
    return html`
      <div class="card" style="margin-top:10px;text-align:start">
        <div><strong>${t('feedback.title')}</strong> <span class="muted small">${t('feedback.sub')}</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:8px">
          ${diff.map((d) => html`<button class="btn ${this.difficulty === d.v ? 'primary' : ''} small"
            style="min-height:30px;text-transform:none" @click=${() => { this.difficulty = d.v; this.saved = false; }}>${d.label}</button>`)}
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:8px">
          ${[
            { v: 5, label: t('useful.5') },
            { v: 3, label: t('useful.3') },
            { v: 1, label: t('useful.1') },
          ].map((o) => html`<button class="btn ${this.useful === o.v ? 'primary' : ''} small" style="min-height:30px;text-transform:none"
            @click=${() => { this.useful = o.v; this.saved = false; }}>${o.label}</button>`)}
        </div>
        <input class="note" style="margin-top:8px" placeholder=${t('feedback.notePlaceholder')}
          .value=${this.note} @input=${(e: Event) => { this.note = (e.target as HTMLInputElement).value; this.saved = false; }} />
        <button class="btn ${this.saved ? '' : 'primary'}" style="margin-top:8px" ?disabled=${this.saving} @click=${this.save}>
          ${this.saved ? t('feedback.savedBtn') : t('feedback.save')}
        </button>
        ${this.msg ? html`<div class="muted small" style="margin-top:6px">${this.msg}</div>` : nothing}
      </div>
    `;
  }
}
